
import * as React from 'react';
import { IItemCacheThunkAction } from '../../store/actions/ItemCacheActions';
import { IItemCacheDispatchProp, connectToItemCache } from '../decorators/connectToItemCache';
import { renderChildrenWithProps } from '../../utilities/components/RenderProps';

/**
 * @public
 */
export interface IItemCacheInitializerPayload<TProps = {}> {
  props: TProps;
  previousProps: TProps | undefined;
}

/**
 * @public
 */
export interface IItemCacheInitializerUnmountPayload<TProps = {}> {
  props: TProps;
}

/**
 * @public
 */
export function createItemCacheInitializer<TProps = {}>(
  onMountAndUpdate?: (payload: IItemCacheInitializerPayload<TProps>) => IItemCacheThunkAction<void>,
  onUnmount?: (payload: IItemCacheInitializerUnmountPayload<TProps>) => IItemCacheThunkAction<void>
): React.ComponentType<TProps> {
  class ItemCacheInitializer extends React.PureComponent<TProps & IItemCacheDispatchProp> {
    public render(): JSX.Element | null {
      return renderChildrenWithProps(this.props.children, {});
    }

    public componentDidMount(): void {
      if (onMountAndUpdate) {
        const {
          itemCacheDispatch,
          ...props
        } = this.props as IItemCacheDispatchProp;

        itemCacheDispatch(onMountAndUpdate({
          props: props as TProps,
          previousProps: undefined
        }));
      }
    }

    public componentDidUpdate({ itemCacheDispatch: _, ...previousProps }: IItemCacheDispatchProp): void {
      if (onMountAndUpdate) {
        const {
          itemCacheDispatch,
          ...props
        } = this.props as IItemCacheDispatchProp;

        itemCacheDispatch(onMountAndUpdate({
          props: props as TProps,
          previousProps: previousProps as TProps
        }));
      }
    }

    public componentWillUnmount(): void {
      if (onUnmount) {
        const {
          itemCacheDispatch,
          ...props
        } = this.props as IItemCacheDispatchProp;

        itemCacheDispatch(onUnmount({
          props: props as TProps
        }));
      }
    }
  }

  return connectToItemCache()(ItemCacheInitializer) as {} as React.ComponentType<TProps>;
}
